import React, { useEffect, useState } from 'react'
import { Tabs, Table, Select, Space, InputNumber, Button, Row, Col, Card, Statistic, Tag, Modal } from 'antd'
import { DownloadOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import api from '../api'

function numFmt(v, precision = 2) {
  return parseFloat(v).toLocaleString('zh-CN', { minimumFractionDigits: precision, maximumFractionDigits: precision })
}

function profitStyle(v) {
  return { color: parseFloat(v) >= 0 ? '#3f8600' : '#cf1322', fontWeight: 'bold' }
}

export default function ReportsPage() {
  const [accounts, setAccounts] = useState([])
  const [projects, setProjects] = useState([])
  const [ownerReport, setOwnerReport] = useState([])
  const [summary, setSummary] = useState(null)
  const [year, setYear] = useState(dayjs().year())
  const [month, setMonth] = useState(null)
  const [rate, setRate] = useState(null)
  const [detailProject, setDetailProject] = useState(null)
  const [detail, setDetail] = useState(null)

  const years = Array.from({ length: 5 }, (_, i) => dayjs().year() - i)
  const months = Array.from({ length: 12 }, (_, i) => i + 1)

  const buildParams = () => {
    const p = new URLSearchParams({ year })
    if (month) p.append('month', String(month).padStart(2, '0'))
    if (rate) p.append('rate', rate)
    return p.toString()
  }

  const loadReports = () => {
    const params = buildParams()
    api.get(`/reports/accounts?start_month=${year}-01&end_month=${year}-12`).then(setAccounts)
    api.get(`/reports/projects?${params}`).then(setProjects)
    api.get(`/reports/summary?${params}`).then(setSummary)
    api.get(`/reports/owners?${params}`).then(setOwnerReport)
  }

  useEffect(() => { loadReports() }, [year, month, rate])

  const exportUrl = (path) => `/api/export/${path}?${buildParams()}`

  const openDetail = async (project) => {
    setDetailProject(project)
    setDetail(null)
    const params = buildParams()
    const data = await api.get(`/reports/projects/${project.id}/detail?${params}`)
    setDetail(data)
  }

  const downloadFile = async (url, filename) => {
    const res = await fetch(url, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
    if (!res.ok) { return }
    const blob = await res.blob()
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = filename
    a.click()
    setTimeout(() => URL.revokeObjectURL(a.href), 10000)
  }

  // 汇总行计算
  const projectTotals = projects.reduce((acc, r) => ({
    income_rmb: acc.income_rmb + parseFloat(r.income_rmb || 0),
    income_vnd: acc.income_vnd + parseFloat(r.income_vnd || 0),
    expense_rmb: acc.expense_rmb + parseFloat(r.expense_rmb || 0),
    expense_vnd: acc.expense_vnd + parseFloat(r.expense_vnd || 0),
    profit_rmb: acc.profit_rmb + parseFloat(r.profit_rmb || 0),
    profit_vnd: acc.profit_vnd + parseFloat(r.profit_vnd || 0),
  }), { income_rmb: 0, income_vnd: 0, expense_rmb: 0, expense_vnd: 0, profit_rmb: 0, profit_vnd: 0 })

  const ownerTotals = ownerReport.reduce((acc, r) => ({
    income_rmb: acc.income_rmb + parseFloat(r.income_rmb || 0),
    income_vnd: acc.income_vnd + parseFloat(r.income_vnd || 0),
    expense_rmb: acc.expense_rmb + parseFloat(r.expense_rmb || 0),
    expense_vnd: acc.expense_vnd + parseFloat(r.expense_vnd || 0),
    profit_rmb: acc.profit_rmb + parseFloat(r.profit_rmb || 0),
    profit_vnd: acc.profit_vnd + parseFloat(r.profit_vnd || 0),
  }), { income_rmb: 0, income_vnd: 0, expense_rmb: 0, expense_vnd: 0, profit_rmb: 0, profit_vnd: 0 })

  const accountColumns = [
    { title: '账户', dataIndex: 'name' },
    { title: '币种', dataIndex: 'currency', width: 80 },
    { title: '期初余额', dataIndex: 'initial_balance', render: v => numFmt(v) },
    { title: '收入', dataIndex: 'total_income', render: v => numFmt(v) },
    { title: '支出', dataIndex: 'total_expense', render: v => numFmt(v) },
    { title: '余额', dataIndex: 'balance', render: v => <span style={profitStyle(v)}>{numFmt(v)}</span> },
  ]

  const projectColumns = [
    { title: '项目', dataIndex: 'name', render: (v, r) => <a onClick={() => openDetail(r)}>{v}</a> },
    { title: '负责人', dataIndex: 'owners', width: 160, render: owners => owners?.length ? owners.map(o => <Tag key={o.id}>{o.name}</Tag>) : '-' },
    { title: '区域', dataIndex: 'region', width: 80, render: v => v === 'domestic' ? '国内' : '国外' },
    { title: '收入RMB', dataIndex: 'income_rmb', render: v => numFmt(v) },
    { title: '收入VND', dataIndex: 'income_vnd', render: v => numFmt(v, 0) },
    { title: '支出RMB', dataIndex: 'expense_rmb', render: v => numFmt(v) },
    { title: '支出VND', dataIndex: 'expense_vnd', render: v => numFmt(v, 0) },
    { title: '盈亏RMB', dataIndex: 'profit_rmb', render: v => <span style={profitStyle(v)}>{numFmt(v)}</span> },
    { title: '盈亏VND', dataIndex: 'profit_vnd', render: v => <span style={profitStyle(v)}>{numFmt(v, 0)}</span> },
  ]

  const ownerColumns = [
    { title: '负责人', dataIndex: 'owner', width: 120 },
    { title: '收入RMB', dataIndex: 'income_rmb', render: v => numFmt(v) },
    { title: '收入VND', dataIndex: 'income_vnd', render: v => numFmt(v, 0) },
    { title: '支出RMB', dataIndex: 'expense_rmb', render: v => numFmt(v) },
    { title: '支出VND', dataIndex: 'expense_vnd', render: v => numFmt(v, 0) },
    { title: '盈亏RMB', dataIndex: 'profit_rmb', render: v => <span style={profitStyle(v)}>{numFmt(v)}</span> },
    { title: '盈亏VND', dataIndex: 'profit_vnd', render: v => <span style={profitStyle(v)}>{numFmt(v, 0)}</span> },
  ]

  // 汇总行渲染
  const SummaryRow = ({ totals, rate, colSpan = 1 }) => {
    const totalProfitRmb = rate ? totals.profit_rmb + totals.profit_vnd / parseFloat(rate) : null
    return (
      <Table.Summary.Row style={{ background: '#fff7e6', fontWeight: 'bold' }}>
        <Table.Summary.Cell index={0} colSpan={colSpan}>合计</Table.Summary.Cell>
        <Table.Summary.Cell index={1}>{numFmt(totals.income_rmb)}</Table.Summary.Cell>
        <Table.Summary.Cell index={2}>{numFmt(totals.income_vnd, 0)}</Table.Summary.Cell>
        <Table.Summary.Cell index={3}>{numFmt(totals.expense_rmb)}</Table.Summary.Cell>
        <Table.Summary.Cell index={4}>{numFmt(totals.expense_vnd, 0)}</Table.Summary.Cell>
        <Table.Summary.Cell index={5}><span style={profitStyle(totals.profit_rmb)}>{numFmt(totals.profit_rmb)}</span></Table.Summary.Cell>
        <Table.Summary.Cell index={6}>
          <span style={profitStyle(totals.profit_vnd)}>{numFmt(totals.profit_vnd, 0)}</span>
          {totalProfitRmb != null && (
            <div style={{ fontSize: 11, color: '#888' }}>折合RMB: <span style={profitStyle(totalProfitRmb)}>{numFmt(totalProfitRmb)}</span></div>
          )}
        </Table.Summary.Cell>
      </Table.Summary.Row>
    )
  }

  const filters = (
    <Space style={{ marginBottom: 16 }} wrap>
      <span>年度：</span>
      <Select value={year} onChange={setYear} style={{ width: 90 }}>
        {years.map(y => <Select.Option key={y} value={y}>{y}</Select.Option>)}
      </Select>
      <span>月份：</span>
      <Select value={month} onChange={setMonth} allowClear placeholder="全年" style={{ width: 80 }}>
        {months.map(m => <Select.Option key={m} value={m}>{m}月</Select.Option>)}
      </Select>
      <span>汇率(RMB→VND)：</span>
      <InputNumber value={rate} onChange={setRate} placeholder="用于折算总计" min={0} style={{ width: 150 }} />
    </Space>
  )

  return (
    <div>
      {filters}
      <Tabs items={[
        {
          key: 'summary', label: '总账汇总',
          children: summary && (
            <Row gutter={16}>
              <Col span={6}><Card><Statistic title="收入(RMB)" value={summary.income_rmb} precision={2} prefix="¥" /></Card></Col>
              <Col span={6}><Card><Statistic title="收入(VND)" value={summary.income_vnd} precision={0} /></Card></Col>
              <Col span={6}><Card><Statistic title="支出(RMB)" value={summary.expense_rmb} precision={2} prefix="¥" /></Card></Col>
              <Col span={6}><Card><Statistic title="支出(VND)" value={summary.expense_vnd} precision={0} /></Card></Col>
              <Col span={8} style={{ marginTop: 16 }}><Card><Statistic title="盈亏(RMB)" value={summary.profit_rmb} precision={2} prefix="¥" valueStyle={{ color: summary.profit_rmb >= 0 ? '#3f8600' : '#cf1322' }} /></Card></Col>
              <Col span={8} style={{ marginTop: 16 }}><Card><Statistic title="盈亏(VND)" value={summary.profit_vnd} precision={0} valueStyle={{ color: summary.profit_vnd >= 0 ? '#3f8600' : '#cf1322' }} /></Card></Col>
              {summary.total_profit_rmb !== undefined && (
                <Col span={8} style={{ marginTop: 16 }}><Card><Statistic title="折算总盈亏(RMB)" value={summary.total_profit_rmb} precision={2} prefix="¥" valueStyle={{ color: summary.total_profit_rmb >= 0 ? '#3f8600' : '#cf1322' }} /></Card></Col>
              )}
            </Row>
          )
        },
        {
          key: 'accounts', label: '账户报表',
          children: (
            <>
              <Space style={{ marginBottom: 8 }}>
                <Button icon={<DownloadOutlined />} onClick={() => downloadFile(`/api/export/income?${buildParams()}`, '收入记录.xlsx')}>导出收入</Button>
                <Button icon={<DownloadOutlined />} onClick={() => downloadFile(`/api/export/expenses?${buildParams()}`, '支出记录.xlsx')}>导出支出</Button>
              </Space>
              <Table
                dataSource={accounts} columns={accountColumns} rowKey="id" pagination={false}
                summary={() => {
                  const rmb = accounts.filter(a => a.currency === 'RMB')
                  const vnd = accounts.filter(a => a.currency === 'VND')
                  const sum = (arr, key) => arr.reduce((s, r) => s + parseFloat(r[key] || 0), 0)
                  const rows = [
                    { label: 'RMB 合计', income: sum(rmb, 'total_income'), expense: sum(rmb, 'total_expense'), balance: sum(rmb, 'balance') },
                    { label: 'VND 合计', income: sum(vnd, 'total_income'), expense: sum(vnd, 'total_expense'), balance: sum(vnd, 'balance') },
                  ]
                  return rows.map(r => (
                    <Table.Summary.Row key={r.label} style={{ background: '#fff7e6', fontWeight: 'bold' }}>
                      <Table.Summary.Cell index={0}>{r.label}</Table.Summary.Cell>
                      <Table.Summary.Cell index={1} />
                      <Table.Summary.Cell index={2} />
                      <Table.Summary.Cell index={3}>{numFmt(r.income)}</Table.Summary.Cell>
                      <Table.Summary.Cell index={4}>{numFmt(r.expense)}</Table.Summary.Cell>
                      <Table.Summary.Cell index={5}><span style={profitStyle(r.balance)}>{numFmt(r.balance)}</span></Table.Summary.Cell>
                    </Table.Summary.Row>
                  ))
                }}
              />
            </>
          )
        },
        {
          key: 'projects', label: '项目盈亏',
          children: (
            <>
              <Button icon={<DownloadOutlined />} onClick={() => downloadFile(`/api/export/projects?${buildParams()}`, '项目盈亏.xlsx')} style={{ marginBottom: 8 }}>导出Excel</Button>
              <Table
                dataSource={projects} columns={projectColumns} rowKey="id" scroll={{ x: 900 }}
                summary={() => <SummaryRow totals={projectTotals} rate={rate} colSpan={3} />}
              />
            </>
          )
        },
        {
          key: 'owners', label: '负责人盈亏',
          children: (
            <>
              <Button icon={<DownloadOutlined />} onClick={() => downloadFile(`/api/export/owners?${buildParams()}`, '负责人盈亏.xlsx')} style={{ marginBottom: 8 }}>导出Excel</Button>
              <Table
                dataSource={ownerReport} columns={ownerColumns} rowKey="owner_id" scroll={{ x: 700 }}
                summary={() => <SummaryRow totals={ownerTotals} rate={rate} colSpan={1} />}
              />
            </>
          )
        },
      ]} />

      <Modal
        title={detailProject ? `${detailProject.name} — 收支明细` : ''}
        open={!!detailProject}
        onCancel={() => setDetailProject(null)}
        footer={
          <Button
            icon={<DownloadOutlined />}
            onClick={() => {
              const p = new URLSearchParams({ year })
              if (month) p.append('month', String(month).padStart(2, '0'))
              p.append('name', detailProject.name)
              downloadFile(`/api/export/project-detail/${detailProject.id}?${p.toString()}`, `${detailProject.name}_明细.xlsx`)
            }}
          >导出Excel</Button>
        }
        width={900}
      >
        {detail ? (
          <Tabs items={[
            {
              key: 'income', label: `收入 (${detail.income.length})`,
              children: (
                <Table
                  dataSource={detail.income} rowKey="id" size="small" pagination={false} scroll={{ x: 700 }}
                  columns={[
                    { title: '名称', dataIndex: 'name', ellipsis: true },
                    { title: '金额', dataIndex: 'amount', render: (v, r) => `${parseFloat(v).toLocaleString('zh-CN')} ${r.currency}` },
                    { title: '所属月份', dataIndex: 'account_month', width: 100 },
                    { title: '实际日期', dataIndex: 'actual_date', width: 110, render: v => dayjs(v).format('YYYY-MM-DD') },
                    { title: '账户', dataIndex: 'account_name', width: 120 },
                    { title: '类别', dataIndex: 'category_name', width: 100 },
                    { title: '摘要', dataIndex: 'summary', ellipsis: true },
                  ]}
                  summary={() => {
                    const rmb = detail.income.filter(r => r.currency === 'RMB').reduce((s, r) => s + parseFloat(r.amount), 0)
                    const vnd = detail.income.filter(r => r.currency === 'VND').reduce((s, r) => s + parseFloat(r.amount), 0)
                    return (
                      <Table.Summary.Row style={{ fontWeight: 'bold', background: '#f6ffed' }}>
                        <Table.Summary.Cell index={0}>合计</Table.Summary.Cell>
                        <Table.Summary.Cell index={1}>
                          {rmb > 0 && <div>RMB {numFmt(rmb)}</div>}
                          {vnd > 0 && <div>VND {numFmt(vnd, 0)}</div>}
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={2} colSpan={5} />
                      </Table.Summary.Row>
                    )
                  }}
                />
              )
            },
            {
              key: 'expense', label: `支出 (${detail.expense.length})`,
              children: (
                <Table
                  dataSource={detail.expense} rowKey="id" size="small" pagination={false} scroll={{ x: 700 }}
                  columns={[
                    { title: '名称', dataIndex: 'name', ellipsis: true },
                    { title: '分摊金额', dataIndex: 'amount', render: (v, r) => `${parseFloat(v).toLocaleString('zh-CN')} ${r.currency}` },
                    { title: '比例', dataIndex: 'ratio', width: 70, render: v => `${v}%` },
                    { title: '所属月份', dataIndex: 'account_month', width: 100 },
                    { title: '实际日期', dataIndex: 'actual_date', width: 110, render: v => dayjs(v).format('YYYY-MM-DD') },
                    { title: '账户', dataIndex: 'account_name', width: 120 },
                    { title: '类别', dataIndex: 'category_name', width: 100 },
                    { title: '摘要', dataIndex: 'summary', ellipsis: true },
                  ]}
                  summary={() => {
                    const rmb = detail.expense.filter(r => r.currency === 'RMB').reduce((s, r) => s + parseFloat(r.amount), 0)
                    const vnd = detail.expense.filter(r => r.currency === 'VND').reduce((s, r) => s + parseFloat(r.amount), 0)
                    return (
                      <Table.Summary.Row style={{ fontWeight: 'bold', background: '#fff1f0' }}>
                        <Table.Summary.Cell index={0}>合计</Table.Summary.Cell>
                        <Table.Summary.Cell index={1}>
                          {rmb > 0 && <div>RMB {numFmt(rmb)}</div>}
                          {vnd > 0 && <div>VND {numFmt(vnd, 0)}</div>}
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={2} colSpan={6} />
                      </Table.Summary.Row>
                    )
                  }}
                />
              )
            }
          ]} />
        ) : <div style={{ textAlign: 'center', padding: 40 }}>加载中...</div>}
      </Modal>
    </div>
  )
}
